import * as React from 'react';
import { cn } from '../../utils/cn';

type LinkVariant = 'default' | 'primary' | 'muted' | 'nav';
type LinkUnderline = 'always' | 'hover' | 'none';
type LinkWeight = 'normal' | 'medium' | 'semibold' | 'bold';

export interface LinkProps {
  href?: string;
  to?: string; // For router integration
  variant?: LinkVariant;
  external?: boolean;
  download?: boolean | string;
  underline?: LinkUnderline;
  weight?: LinkWeight;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  children: React.ReactNode;
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

const variantClasses: Record<LinkVariant, string> = {
  default: 'text-primary hover:text-primary-focus',
  primary: 'text-primary hover:text-primary-focus',
  muted: 'text-base-content/70 hover:text-base-content',
  nav: 'text-base-content hover:text-primary transition-colors focus:!ring-offset-0',
};

const underlineClasses: Record<LinkUnderline, string> = {
  always: 'underline',
  hover: 'no-underline hover:underline',
  none: 'no-underline',
};

const weightClasses: Record<LinkWeight, string> = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
};

export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      href,
      to,
      variant = 'default',
      external = false,
      download,
      underline = 'hover',
      weight = 'normal',
      icon,
      iconPosition = 'right',
      children,
      className,
      onClick,
    },
    ref
  ) => {
    const linkHref = href || to || '#';
    const isExternal = external || (typeof linkHref === 'string' && linkHref.startsWith('http'));

    const externalProps = isExternal
      ? {
          target: '_blank',
          rel: 'noopener noreferrer',
        }
      : {};

    const downloadProps = download
      ? {
          download: typeof download === 'string' ? download : true,
        }
      : {};

    const iconElement = icon && (
      <span className={cn('inline-flex items-center', iconPosition === 'left' ? 'mr-1' : 'ml-1')}>
        {icon}
      </span>
    );

    return (
      <a
        ref={ref}
        href={linkHref}
        className={cn(
          variant === 'nav'
            ? 'flex items-center focus-ring'
            : 'inline-flex items-center focus-ring',
          variantClasses[variant],
          underlineClasses[underline],
          weightClasses[weight],
          'transition-colors duration-200',
          'focus:!outline-none focus:!ring-1 focus:!ring-primary focus:!ring-offset-1',
          className
        )}
        onClick={onClick}
        {...externalProps}
        {...downloadProps}
      >
        {iconPosition === 'left' && iconElement}
        {children}
        {iconPosition === 'right' && iconElement}
      </a>
    );
  }
);

Link.displayName = 'Link';
